import { Alert, Button, Card, DatePicker, Descriptions, Form, Select, Space, Table, Typography } from 'antd'
import { useState } from 'react'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import { workAreaStatusOptions } from '../../invoicing/workAreaStatusModel'
import { useJobsToInvoiceCompareMutation } from './jobsToInvoiceCompareQueries'
import { JobsToInvoiceComparison } from './jobsToInvoiceCompareApi'
import {
  CompareDateRange,
  DifferenceRow,
  getCompareErrorMessage,
  getComparisonSummary,
  toCompareRequest,
  toDifferenceRows,
} from './jobsToInvoiceCompareModel'

const { Paragraph, Text } = Typography

const differenceColumns = [
  { title: 'Job', dataIndex: 'jobId', key: 'jobId', width: 100, render: (jobId: number) => `#${jobId}` },
  { title: 'Field', dataIndex: 'field', key: 'field', width: 180 },
  { title: 'Current', dataIndex: 'current', key: 'current' },
  { title: 'Facts', dataIndex: 'facts', key: 'facts' },
]

const JobIdList = ({ jobIds }: { jobIds: number[] }) => {
  if (jobIds.length === 0) {
    return <Text type="secondary">None</Text>
  }
  return <Text>{jobIds.map(jobId => `#${jobId}`).join(', ')}</Text>
}

const ComparisonResult = ({ comparison }: { comparison: JobsToInvoiceComparison }) => (
  <Space direction="vertical" size={16} style={{ width: '100%' }}>
    <Alert
      type={comparison.isIdentical ? 'success' : 'warning'}
      showIcon
      message={comparison.isIdentical ? 'Identical' : 'Not identical'}
      description={getComparisonSummary(comparison)}
    />
    <Descriptions bordered size="small" column={1}>
      <Descriptions.Item label="Current job count">{comparison.currentJobCount}</Descriptions.Item>
      <Descriptions.Item label="Facts job count">{comparison.factsJobCount}</Descriptions.Item>
      <Descriptions.Item label="Missing from facts">
        <JobIdList jobIds={comparison.missingFromFacts} />
      </Descriptions.Item>
      <Descriptions.Item label="Extra in facts">
        <JobIdList jobIds={comparison.extraInFacts} />
      </Descriptions.Item>
    </Descriptions>
    {comparison.differences.length > 0 && (
      <Table<DifferenceRow>
        size="small"
        title={() => 'Field differences'}
        columns={differenceColumns}
        dataSource={toDifferenceRows(comparison)}
        pagination={false}
      />
    )}
  </Space>
)

const JobsToInvoiceCompareSection = () => {
  const [tenantId, setTenantId] = useState<number | undefined>(undefined)
  const [dateRange, setDateRange] = useState<CompareDateRange>(null)
  const [divisionGroups, setDivisionGroups] = useState<string[]>([])
  const [workAreaStatuses, setWorkAreaStatuses] = useState<number[]>([])
  const tenantsQuery = useSidebarTenantsQuery()
  const mutation = useJobsToInvoiceCompareMutation()

  const request = toCompareRequest({ tenantId, dateRange, divisionGroups, workAreaStatuses })

  const resetOnChange = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value)
    mutation.reset()
  }

  const handleCompare = () => {
    if (request === null) {
      return
    }
    mutation.mutate(request)
  }

  return (
    <Card title="Compare Jobs to Invoice" style={{ maxWidth: 960 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Checks the jobs-to-invoice facts against the current discovery for a tenant before turning
          on the jobs-to-invoice-facts flag. Leave a filter empty to apply no filter, like the Ready to
          Invoice screen.
        </Paragraph>

        <Form layout="vertical" style={{ marginBottom: 0 }}>
          <Form.Item label="Tenant">
            <Select
              showSearch
              placeholder="Search for a tenant"
              value={tenantId}
              onChange={resetOnChange(setTenantId)}
              options={toTenantOptions(tenantsQuery.data ?? [])}
              filterOption={filterTenantOption}
              loading={tenantsQuery.isLoading}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="Date range">
            <DatePicker.RangePicker
              format="MM/DD/YYYY"
              value={dateRange}
              onChange={resetOnChange(setDateRange)}
              allowEmpty={[false, false]}
            />
          </Form.Item>
          <Form.Item label="Division groups" extra="Type each division group name exactly and press Enter.">
            <Select
              mode="tags"
              placeholder="All division groups"
              value={divisionGroups}
              onChange={resetOnChange(setDivisionGroups)}
              open={false}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="Work area statuses" style={{ marginBottom: 0 }}>
            <Select
              mode="multiple"
              placeholder="All work area statuses"
              value={workAreaStatuses}
              onChange={resetOnChange(setWorkAreaStatuses)}
              options={workAreaStatusOptions.map(option => ({ label: option.name, value: option.value }))}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>

        <Button
          type="primary"
          onClick={handleCompare}
          loading={mutation.isLoading}
          disabled={request === null}
        >
          Run comparison
        </Button>

        {mutation.isSuccess && <ComparisonResult comparison={mutation.data} />}

        {mutation.isError && (
          <Alert
            type="error"
            showIcon
            message="Comparison failed"
            description={getCompareErrorMessage(mutation.error)}
          />
        )}
      </Space>
    </Card>
  )
}

export default JobsToInvoiceCompareSection
